import { useMemo } from 'react';
import { useLens } from '@hookform/lenses';
import { zodResolver } from '@hookform/resolvers/zod';
import { createFileRoute, Link, useBlocker } from '@tanstack/react-router';
import { FormProvider, type Path, useForm } from 'react-hook-form';
import { useIxcRaglV1ManualEntryCommandServiceCreateManualEntry } from '@interxfi/api-client/generated/hooks/ixc-ragl-v1-manual-entry-command-service-create-manual-entry';
import { FormLayout } from '@interxfi/app-components/components/layout/action-layout/form-layout/form-layout';
import { DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { useButtonState } from '@interxfi/design-system/components/button';
import { useAddToast } from '@interxfi/design-system/components/toast/toast-context';
import {
  formValuesToRaglManualEntrySpec,
  getInitialRaglManualEntrySpecFormDefaultValues,
  RaglManualEntrySpecForm,
  type RaglManualEntrySpecFormValues,
  raglManualEntrySpecFormOnSubmitSchema,
  raglManualEntrySpecFormSchema,
} from '../../../../../../../../../features/clearing-ledger/manual-entry/form/ragl-manual-entry-spec-form';
import { RaglManualEntryCancelCreateAlertDialog } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-cancel-create-alert-dialog';
import { RAGL_MANUAL_ENTRY_BASE_PATH } from '../../../../../../../../../features/clearing-ledger/manual-entry/ragl-manual-entry-routing';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/operations/_operations/journal/_manual-entry/create')({
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'new-ragl-manual-entry',
        label: 'New Manual Entry',
        to: `${RAGL_MANUAL_ENTRY_BASE_PATH}/create`,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Create Manual Entry'),
  component: RaglCreateManualEntry,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to create manual entry', 'create a manual entry');

function RaglCreateManualEntry() {
  const navigate = Route.useNavigate();
  const addToast = useAddToast();
  const { data, mutateAsync, isPending, error } = useIxcRaglV1ManualEntryCommandServiceCreateManualEntry();
  useErrorHandler(error, errorMessageGetter);
  const [buttonState, buttonRef] = useButtonState(isPending, error);
  const defaultValues = useMemo(() => getInitialRaglManualEntrySpecFormDefaultValues(), []);

  const form = useForm<RaglManualEntrySpecFormValues>({
    mode: 'all',
    defaultValues,
    resolver: zodResolver(raglManualEntrySpecFormSchema),
  });
  const {
    control,
    formState: { isDirty, isValid },
    handleSubmit,
    setError,
  } = form;
  const lens = useLens({ control });

  const onSubmit = async (values: RaglManualEntrySpecFormValues) => {
    const { success, error } = raglManualEntrySpecFormOnSubmitSchema.safeParse(values);
    if (!success) {
      error.issues?.forEach((issue) => {
        setError(issue.path.join('.') as Path<RaglManualEntrySpecFormValues>, { message: issue.message });
      });

      addToast({
        id: 'createManualEntryError',
        variant: 'error',
        content: error.issues?.[0]?.message || 'There is a problem with the data you provided, please try again.',
      });

      return;
    }

    try {
      const res = await mutateAsync({ spec: formValuesToRaglManualEntrySpec(values) });
      if (res?.manualEntry?.entryId) {
        addToast({ id: 'manualEntryCreated', content: 'Manual entry created', variant: 'success' });
        await navigate({ to: '/clearing/operations/journal/$entryId', params: { entryId: res.manualEntry.entryId } });
      }
    } catch {}
  };

  const {
    proceed,
    reset: stopNavigation,
    status,
  } = useBlocker({
    shouldBlockFn: ({ next }) => {
      if (isDirty && !data?.manualEntry) {
        return !next.fullPath.startsWith('/clearing/operations/journal/$entryId');
      }

      return false;
    },
    enableBeforeUnload: false,
    withResolver: true,
  });

  return (
    <FormProvider {...form}>
      <FormLayout
        actions={[
          {
            buttonLink: {
              id: 'cancel',
              children: 'Cancel',
              LinkElement: Link,
              to: '/clearing/operations/journal',
              variant: 'secondary',
            },
          },
          {
            button: {
              id: 'createManualEntry',
              children: 'Create Manual Entry',
              disabled: !isValid,
              ref: buttonRef,
              state: buttonState,
              type: 'submit',
              variant: 'primary',
            },
          },
        ]}
        maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}
        onSubmit={handleSubmit(onSubmit)}
      >
        <RaglManualEntrySpecForm lens={lens} />
      </FormLayout>

      <RaglManualEntryCancelCreateAlertDialog isOpen={status === 'blocked'} onCancel={stopNavigation} onConfirm={proceed} trigger={null} />
    </FormProvider>
  );
}
